import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getDialogStyles } from './styles';
import { InstallGuide } from './InstallGuide';
import { getDefaultGroupIdForTopic, getDifficultyOptionsForTopic, getUniqueTopics } from '../groupSelection';
import { formatLastReviewed } from '../../context/FlashcardContext/studyStats';

function SettingsDialog({ 
  isOpen, 
  onClose, 
  activeGroup, 
  groups = [], 
  allFlashcards = [],
  onGroupChange,
  onManageCards,
  isMuted,
  onToggleMute,
  onInstallClick,
  pushSupported = false,
  pushEnabled = false,
  pushBusy = false,
  onTogglePushNotifications = () => {},
  reminderSettings = {
    reminderEnabled: false,
    reminderTime: '19:00',
    timezone: 'UTC',
  },
  onReminderToggle = () => {},
  onReminderTimeChange = () => {},
  pushStatusMessage = '',
  onChallengeClick = () => {},
  challengeEnabled = false,
  shareMessage = '',
  isChallengeMode = false,
  onExitChallenge = () => {},
  studyStats = {
    currentStreak: 0,
    longestStreak: 0,
    totalAnswers: 0,
    studyDays: 0,
  },
}) {
  const { isDark, toggleTheme, colors } = useTheme();
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showStatsView, setShowStatsView] = useState(false);
  
  if (!isOpen) return null;

  const styles = getDialogStyles(colors);
  const activeGroupData = groups.find(group => group.id === activeGroup);
  const topics = getUniqueTopics(groups);
  const selectedTopic = activeGroupData?.topic || topics[0] || '';
  const difficultyOptions = getDifficultyOptionsForTopic(groups, selectedTopic);
  const groupCards = activeGroup ? allFlashcards.filter(card => card.groupId === activeGroup) : [];
  const reviewedCount = groupCards.filter(card => card.reviewed).length;
  const completion = groupCards.length ? Math.round((reviewedCount / groupCards.length) * 100) : 0;

  if (showInstallGuide) {
    return (
      <InstallGuide 
        onBack={() => setShowInstallGuide(false)} 
        onClose={onClose}
        colors={colors}
      />
    );
  }

  if (showStatsView) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.dialog} onClick={e => e.stopPropagation()}>
          <div style={styles.topBar}>
            <button
              onClick={() => setShowStatsView(false)}
              style={styles.iconButton}
              aria-label="Back"
              title="Back"
            >
              ←
            </button>
            {!isChallengeMode && (
              <button
                onClick={onChallengeClick}
                style={{
                  ...styles.iconButton,
                  ...(challengeEnabled ? {} : styles.disabledButton)
                }}
                disabled={!challengeEnabled}
                aria-label="Share challenge"
                title="Share challenge"
              >
                🔗
              </button>
            )}
            {pushSupported && (
              <button
                onClick={onTogglePushNotifications}
                style={{
                  ...styles.iconButton,
                  ...(pushEnabled ? styles.activeIconButton : {}),
                  ...(pushBusy ? styles.disabledButton : {})
                }}
                disabled={pushBusy}
                aria-label={pushEnabled ? 'Disable daily reminder' : 'Enable daily reminder'}
                title={pushEnabled ? 'Disable daily reminder' : 'Enable daily reminder'}
              >
                {pushEnabled ? '🔔' : '🔕'}
              </button>
            )}
            <button
              onClick={onToggleMute}
              style={styles.iconButton}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? '🔇' : '🔊'}
            </button>
            <button
              onClick={() => setShowInstallGuide(true)}
              style={styles.iconButton}
              aria-label="Install"
              title="Install"
            >
              ⬇️
            </button>
            <button
              onClick={toggleTheme}
              style={styles.iconButton}
              aria-label={isDark ? 'Light mode' : 'Dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? '🌙' : '☀️'}
            </button>
            <button
              onClick={onClose}
              style={styles.iconButton}
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          </div>

          <div style={styles.selectGrid}>
            <div style={styles.selectContainer}>
              <label style={styles.selectLabel}>Topic</label>
              <select
                value={selectedTopic}
                onChange={(e) => {
                  const nextGroupId = getDefaultGroupIdForTopic(groups, e.target.value);
                  if (nextGroupId) onGroupChange(nextGroupId);
                }}
                style={styles.select}
              >
                {topics.map(topic => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.selectContainer}>
              <label style={styles.selectLabel}>Level</label>
              <select
                value={activeGroup}
                onChange={(e) => onGroupChange(e.target.value)}
                style={styles.select}
              >
                {difficultyOptions.map(option => (
                  <option key={option.groupId} value={option.groupId}>
                    {option.difficulty}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.statsPanel}>
            <div style={styles.statsHeader}>Study Stats</div>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Streak</span>
                <span style={styles.statValue}>{studyStats.currentStreak}</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Answered</span>
                <span style={styles.statValue}>{studyStats.totalAnswers}</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Complete</span>
                <span style={styles.statValue}>{completion}%</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Cards</span>
                <span style={styles.statValue}>{reviewedCount}/{groupCards.length || 0}</span>
              </div>
            </div>
            <div style={styles.statsFooter}>
              Last reviewed {formatLastReviewed(groupCards)}
            </div>
          </div>

          {pushSupported && pushEnabled && (
            <>
              <div style={styles.notificationRow}>
                <span style={styles.notificationText}>
                  Daily reminder {reminderSettings.reminderEnabled ? 'on' : 'off'}
                </span>
                <button
                  onClick={onReminderToggle}
                  style={{
                    ...styles.secondaryMiniButton,
                    ...(pushBusy ? styles.disabledButton : {})
                  }}
                  disabled={pushBusy}
                >
                  {reminderSettings.reminderEnabled ? 'Disable' : 'Enable'}
                </button>
              </div>
              <div style={styles.notificationControls}>
                <label style={styles.selectLabel}>Reminder time</label>
                <input
                  type="time"
                  value={reminderSettings.reminderTime}
                  onChange={(event) => onReminderTimeChange(event.target.value)}
                  style={styles.timeInput}
                  disabled={pushBusy || !reminderSettings.reminderEnabled}
                />
              </div>
            </>
          )}

          {(pushStatusMessage || shareMessage) && (
            <div style={styles.statusMessage}>{pushStatusMessage || shareMessage}</div>
          )}

        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.dialog} onClick={e => e.stopPropagation()}>
        <div style={styles.topBar}>
          {pushSupported && (
            <button
              onClick={onTogglePushNotifications}
              style={{
                ...styles.iconButton,
                ...(pushEnabled ? styles.activeIconButton : {}),
                ...(pushBusy ? styles.disabledButton : {})
              }}
              disabled={pushBusy}
              aria-label={pushEnabled ? 'Disable daily reminder' : 'Enable daily reminder'}
              title={pushEnabled ? 'Disable daily reminder' : 'Enable daily reminder'}
            >
              {pushEnabled ? '🔔' : '🔕'}
            </button>
          )}
          {!isChallengeMode && (
            <button
              onClick={onChallengeClick}
              style={{
                ...styles.iconButton,
                ...(challengeEnabled ? {} : styles.disabledButton)
              }}
              disabled={!challengeEnabled}
              aria-label="Share challenge"
              title="Share challenge"
            >
              🔗
            </button>
          )}
          <button
            onClick={onToggleMute}
            style={styles.iconButton}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>
          <button
            onClick={() => setShowInstallGuide(true)}
            style={styles.iconButton}
            aria-label="Install"
            title="Install"
          >
            ⬇️
          </button>
          <button
            onClick={toggleTheme}
            style={styles.iconButton}
            aria-label={isDark ? 'Light mode' : 'Dark mode'}
            title={isDark ? 'Light mode' : 'Dark mode'}
          >
            {isDark ? '🌙' : '☀️'}
          </button>
          <button
            onClick={onClose}
            style={styles.iconButton}
            aria-label="Close"
            title="Close"
          >
            ✕
          </button>
        </div>

        {!isChallengeMode && groups.length > 0 && (
          <div style={styles.selectGrid}>
            <div style={styles.selectContainer}>
              <label style={styles.selectLabel}>Topic</label>
              <select
                value={selectedTopic}
                onChange={(e) => {
                  const nextGroupId = getDefaultGroupIdForTopic(groups, e.target.value);
                  if (nextGroupId) onGroupChange(nextGroupId);
                }}
                style={styles.select}
              >
                {topics.map(topic => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.selectContainer}>
              <label style={styles.selectLabel}>Level</label>
              <select
                value={activeGroup}
                onChange={(e) => onGroupChange(e.target.value)}
                style={styles.select}
              >
                {difficultyOptions.map(option => (
                  <option key={option.groupId} value={option.groupId}>
                    {option.difficulty}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        
        <div style={styles.menuGrid}>
          {!isChallengeMode && (
            <button onClick={onManageCards} style={styles.menuButton}>
              <span style={styles.menuIcon}>📚</span>
              <span style={styles.menuText}>Sets</span>
            </button>
          )}

          {!isChallengeMode && (
            <button onClick={() => setShowStatsView(true)} style={styles.menuButton}>
              <span style={styles.menuIcon}>📊</span>
              <span style={styles.menuText}>Stats</span>
            </button>
          )}

          {isChallengeMode && (
            <button onClick={onExitChallenge} style={{ ...styles.menuButton, ...styles.primaryButton }}>
              <span style={styles.menuIcon}>↩</span>
              <span style={styles.menuText}>Exit</span>
            </button>
          )}
        </div>

        {pushSupported && (
          <>
            <div style={styles.notificationRow}>
              <span style={styles.notificationText}>
                {pushEnabled
                  ? `Daily ${reminderSettings.reminderEnabled ? 'on' : 'off'}`
                  : 'Daily reminder off'}
              </span>
              {pushEnabled && (
                <button
                  onClick={onReminderToggle}
                  style={{
                    ...styles.secondaryMiniButton,
                    ...(pushBusy ? styles.disabledButton : {})
                  }}
                  disabled={pushBusy}
                >
                  {reminderSettings.reminderEnabled ? 'Disable' : 'Enable'}
                </button>
              )}
            </div>
            {pushEnabled && (
              <div style={styles.notificationControls}>
                <input
                  type="time"
                  value={reminderSettings.reminderTime}
                  onChange={(event) => onReminderTimeChange(event.target.value)}
                  style={styles.timeInput}
                  disabled={pushBusy || !reminderSettings.reminderEnabled}
                />
              </div>
            )}
          </>
        )}

        {(pushStatusMessage || shareMessage) && (
          <div style={styles.statusMessage}>{pushStatusMessage || shareMessage}</div>
        )}

        <div style={styles.creditLinks}>
          <a
            href="https://anadi.info/"
            target="_blank"
            rel="noreferrer"
            style={styles.creditLink}
          >
            Made by anadi.info with love ❤️
          </a>
          <a
            href="https://anadi.info/work"
            target="_blank"
            rel="noreferrer"
            style={styles.creditLink}
          >
            More apps
          </a>
        </div>
      </div>
    </div>
  );
}

export default SettingsDialog;
